"use client";

import { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Loader2 } from "lucide-react";
import { exportWithOptions, type ExportOptions, type ExportFormat } from "@/services/exportImportService";
import { SelfAnchoredPopover } from "@/components/tailwind/ui/anchored-popover";
import { useWorkspaceContext } from "@/contexts/workspaceContext";

interface ExportOptionsModalProps {
  pageId: string;
  pageTitle: string;
  blockType?: "page" | "collection_view";
  onClose: () => void;
}

// ── Inline dropdown ────────────────────────────────────────────────────────────

function Dropdown({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-1 text-sm text-zinc-700 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-zinc-100 focus:outline-none"
      >
        {selected?.label}
        <ChevronDown className="h-3.5 w-3.5 text-zinc-400 mt-px" />
      </button>

      {open && (
        <SelfAnchoredPopover
          placement="bottom-end"
          onClose={() => setOpen(false)}
          className="min-w-[180px] bg-white dark:bg-zinc-800 border border-component rounded-lg shadow-lg py-1 overflow-hidden"
        >
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => { onChange(o.value); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-sm transition-colors ${
                o.value === value
                  ? "bg-zinc-100 dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 font-medium"
                  : "text-zinc-700 dark:text-zinc-300 hover-component "
              }`}
            >
              {o.label}
            </button>
          ))}
        </SelfAnchoredPopover>
      )}
    </div>
  );
}

// ── Toggle ────────────────────────────────────────────────────────────────────

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-[22px] w-10 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
        checked ? "bg-blue-500" : "bg-zinc-300 dark:bg-zinc-600"
      }`}
    >
      <span
        className={`inline-block h-[18px] w-[18px] transform rounded-full bg-white shadow-sm transition duration-200 ${
          checked ? "translate-x-[18px]" : "translate-x-0"
        }`}
      />
    </button>
  );
}

// ── Row ───────────────────────────────────────────────────────────────────────

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between h-9">
      <span className="text-sm text-zinc-600 dark:text-zinc-400">{label}</span>
      <div className="flex-shrink-0">{children}</div>
    </div>
  );
}

// ── Options ───────────────────────────────────────────────────────────────────

const FORMAT_OPTIONS = [
  { value: "markdown_csv", label: "Markdown & CSV" },
  { value: "html", label: "HTML" },
  { value: "pdf", label: "PDF" },
];

const PAGE_CONTENT_OPTIONS = [
  { value: "everything", label: "Everything" },
  { value: "exclude_files_images", label: "Exclude files and images" },
];

const PAGE_FORMAT_OPTIONS = [
  { value: "letter", label: "Letter" },
  { value: "a4", label: "A4" },
  { value: "a3", label: "A3" },
  { value: "tabloid", label: "Tabloid" },
  { value: "legal", label: "Legal" },
];

// ── Modal ─────────────────────────────────────────────────────────────────────

export default function ExportOptionsModal({
  pageId,
  pageTitle,
  blockType = "page",
  onClose,
}: ExportOptionsModalProps) {
  const { currentWorkspace } = useWorkspaceContext();
  const workspaceId = currentWorkspace?._id || "";

  const [format, setFormat] = useState<ExportFormat>("markdown_csv");
  const [pageContent, setPageContent] = useState<"everything" | "exclude_files_images">("everything");
  const [includeSubpages, setIncludeSubpages] = useState(false);
  const [createFolders, setCreateFolders] = useState(true);
  const [pageFormat, setPageFormat] = useState("letter");
  const [scalePercent, setScalePercent] = useState(100);
  const [isLoading, setIsLoading] = useState(false);

  const isPage = blockType === "page";
  const isPdf = isPage && format === "pdf";

  const handleExport = async () => {
    if (!workspaceId) return;
    setIsLoading(true);
    const opts: ExportOptions = {
      format,
      pageContent,
      includeSubpages,
      createFolders,
      pageFormat,
      scalePercent,
    };
    await exportWithOptions(pageId, pageTitle, workspaceId, opts, blockType);
    setIsLoading(false);
    onClose();
  };

  return createPortal(
    <div
      // Bottom-anchored on mobile, centered on desktop.
      className="fixed inset-0 z-[9999] flex items-end md:items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Mobile: full width, top corners only, max 90dvh + scroll.
          Desktop: original 340px width, fully rounded. `overflow-visible`
          desktop keeps any nested format-picker popover from being clipped. */}
      <div className="bg-white dark:bg-zinc-900 shadow-2xl border border-component w-full md:w-[340px] rounded-t-xl md:rounded-xl max-h-[90dvh] md:max-h-none overflow-y-auto md:overflow-visible">

        {/* Rows */}
        <div className="px-4 pt-4 pb-2 space-y-0">

          {isPage && (
            <Row label="Export format">
              <Dropdown value={format} onChange={(v) => setFormat(v as ExportFormat)} options={FORMAT_OPTIONS} />
            </Row>
          )}

          <Row label="Page content">
            <Dropdown value={pageContent} onChange={(v) => setPageContent(v as typeof pageContent)} options={PAGE_CONTENT_OPTIONS} />
          </Row>

          {isPdf && (
            <>
              <Row label="Page format">
                <Dropdown value={pageFormat} onChange={setPageFormat} options={PAGE_FORMAT_OPTIONS} />
              </Row>
              <Row label="Scale percent">
                <input
                  type="number"
                  min={10}
                  max={200}
                  value={scalePercent}
                  onChange={(e) => setScalePercent(Number(e.target.value))}
                  className="w-16 text-sm text-right border border-zinc-300 dark:border-zinc-600 rounded-md px-2 py-0.5 bg-transparent text-zinc-700 dark:text-zinc-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </Row>
            </>
          )}

          {isPage && (
            <>
              <Row label="Include subpages">
                <Toggle checked={includeSubpages} onChange={setIncludeSubpages} />
              </Row>

              <Row label="Create folders for subpages">
                <Toggle checked={createFolders} onChange={setCreateFolders} />
              </Row>
            </>
          )}
        </div>

        {/* Divider */}
        <div className="mx-4 border-t border-zinc-100 dark:border-zinc-800 mt-2" />

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 text-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover-component  rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={isLoading}
            className="flex items-center gap-1.5 px-5 py-1.5 bg-blue-500 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium rounded-md transition-colors"
          >
            {isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Export
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
